import { Calendar } from 'lucide-react';
import { Badge } from '../ui/badge';
import { formatCurrencyNoDecimals, formatDate } from '../../utils/formatters';
import { ActivityIcon, ActivityType } from './ActivityIcon';
import { RecentActivity } from '../../services/metricsService';

interface RecentActivityItemProps {
  activity: RecentActivity;
  onClick?: (activity: RecentActivity) => void;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'pending_approval':
      return <Badge variant="warning">Pending Approval</Badge>;
    case 'completed':
      return <Badge variant="success">Completed</Badge>;
    case 'updated':
      return <Badge variant="secondary">Updated</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
};

export function RecentActivityItem({
  activity,
  onClick,
}: RecentActivityItemProps) {
  const isInteractive = Boolean(onClick);

  const handleClick = () => {
    if (onClick) {
      onClick(activity);
    }
  };

  return (
    <div
      className={`flex items-center space-x-4 ${
        isInteractive
          ? 'cursor-pointer hover:bg-accent hover:shadow-sm rounded-md p-2 -mx-2 transition-all duration-150 border border-transparent hover:border-border'
          : ''
      }`}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handleClick();
      }}
    >
      <ActivityIcon type={activity.type as ActivityType} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">
          {activity.description}
        </p>
        <div className="flex items-center space-x-2 mt-1">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">
            {formatDate(activity.date)}
          </p>
          {getStatusBadge(activity.status)}
        </div>
      </div>
      {activity.amount && (
        <div className="text-sm font-medium text-foreground">
          {formatCurrencyNoDecimals(activity.amount, activity.currency)}
        </div>
      )}
    </div>
  );
}
